import { useLocation } from "react-router-dom"
import { breadcrumbConfig, type BreadcrumbEntry } from "@/routes/breadcrumb-config"

export type BreadcrumbItem = {
  label: string
  path: string
  isNavigable: boolean
}

/**
 * Derives an ordered breadcrumb trail from the current URL path.
 *
 * Walks up the `parent` chain defined in `breadcrumbConfig` starting from the
 * current pathname. Returns an array ordered from root to current page.
 *
 * If the current path's config has a `queryParamTail` defined and that param
 * is present in the URL, its value is appended as a final non-navigable crumb.
 *
 * If a path segment is not found in the config, it falls back to title-casing
 * the last URL segment as the label.
 *
 * @returns BreadcrumbItem[]
 */
export function useBreadcrumbs(): BreadcrumbItem[] {
  const { pathname, search } = useLocation()

  if (pathname === "/") {
    return []
  }

  // Current URL query params — used to carry over `preserveParams` onto
  // ancestor crumb links and to extract `queryParamTail` values.
  const currentParams = new URLSearchParams(search)

  const crumbs: BreadcrumbItem[] = []
  let currentPath: string | undefined = pathname

  // Check if the leaf entry has a queryParamTail — we'll append it at the end.
  const leafEntry: BreadcrumbEntry | undefined = breadcrumbConfig[pathname]
  let tailCrumb: BreadcrumbItem | null = null

  if (leafEntry?.queryParamTail) {
    const tailValue = currentParams.get(leafEntry.queryParamTail)
    if (tailValue) {
      tailCrumb = {
        label: tailValue,
        path: `${pathname}?${leafEntry.queryParamTail}=${encodeURIComponent(tailValue)}`,
        isNavigable: false,
      }
    }
  }

  // Walk up the parent chain, collecting crumbs from leaf to root.
  // Guard against infinite loops with a max depth of 10.
  const maxDepth = 10
  let depth = 0

  while (currentPath !== undefined && depth < maxDepth) {
    const entry: BreadcrumbEntry | undefined = breadcrumbConfig[currentPath]

    if (entry) {
      if (!entry.hidden) {
        // Build the crumb path, appending any preserved query params that
        // are present in the current URL.
        let crumbPath = currentPath
        if (entry.preserveParams && entry.preserveParams.length > 0) {
          const preserved = new URLSearchParams()
          for (const key of entry.preserveParams) {
            const value = currentParams.get(key)
            if (value !== null) {
              preserved.set(key, value)
            }
          }
          const query = preserved.toString()
          if (query) {
            crumbPath = `${currentPath}?${query}`
          }
        }

        crumbs.unshift({
          label: entry.label,
          path: crumbPath,
          isNavigable: entry.isNavigable !== false,
        })
      }
      currentPath = entry.parent
    } else {
      // Fallback: path not in config — derive a label from the last segment
      // and continue walking up the URL structure.
      const segments = currentPath.split("/").filter(Boolean)
      const lastSegment = segments.pop()
      if (lastSegment) {
        crumbs.unshift({
          label: formatSegment(lastSegment),
          path: currentPath,
          isNavigable: true,
        })
      }
      // Derive the parent path from the URL (e.g. "/a/b/c" → "/a/b")
      currentPath = segments.length > 0 ? "/" + segments.join("/") : "/"
      // If we've reached root, stop — the title link already serves as "Home"
      if (currentPath === "/") {
        break
      }
    }

    depth++
  }

  // Remove the "Home" root entry — the app title already serves that purpose.
  if (crumbs.length > 0 && crumbs[0].path === "/") {
    crumbs.shift()
  }

  // Append the query param tail crumb if present.
  if (tailCrumb) {
    crumbs.push(tailCrumb)
  }

  return crumbs
}

/**
 * Converts a URL segment slug into a human-readable title.
 * @param segment - A hyphen-separated URL path segment.
 * @returns A title-cased string with hyphens replaced by spaces.
 */
function formatSegment(segment: string): string {
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}
